
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ModuleEditor from './components/ModuleEditor';

interface Module {
  id: number;
  level: string;
  title: string;
  description: string;
  topics: string[];
  subtopics: string[];
  content: string;
  image: string;
}

const initialModules: Module[] = [
  // PRINCIPIANTE - Módulos 1-5
  {
    id: 1,
    level: "PRINCIPIANTE",
    title: "¿Qué es la Inteligencia Artificial?",
    description: "Introducción básica a conceptos fundamentales de IA",
    topics: ["Definición de IA", "Historia básica", "IA en la vida cotidiana"],
    subtopics: ["Asistentes virtuales", "Recomendaciones", "Juegos inteligentes"],
    content: "La Inteligencia Artificial es la capacidad de las máquinas para imitar la inteligencia humana. Los estudiantes aprenderán sobre Siri, Alexa, y cómo Netflix recomienda películas.",
    image: "https://public.readdy.ai/ai/img_res/49e4cb5321b862cd79ffb08654ba56a5.jpg"
  },
  {
    id: 2,
    level: "PRINCIPIANTE",
    title: "Los Robots y las Máquinas Inteligentes",
    description: "Exploración del mundo de los robots y automatización",
    topics: ["Tipos de robots", "Máquinas que aprenden", "Robots ayudantes"],
    subtopics: ["Robots domésticos", "Robots industriales", "Robots de juguete"],
    content: "Descubriremos cómo los robots nos ayudan en casa, en las fábricas y pueden ser nuestros amigos. Exploraremos robots como Roomba, brazos robóticos y mascotas robóticas.",
    image: "https://readdy.ai/api/search-image?query=Friendly%20colorful%20robots%20interacting%20with%20children%20in%20classroom%2C%20cute%20robotic%20helpers%2C%20educational%20robotics%20for%20kids%2C%20playful%20mechanical%20companions%20in%20bright%20learning%20environment&width=400&height=300&seq=friendly-robots-kids&orientation=landscape"
  },
  {
    id: 3,
    level: "PRINCIPIANTE",
    title: "Juegos y Aplicaciones Inteligentes",
    description: "IA en entretenimiento y aplicaciones móviles",
    topics: ["Juegos con IA", "Apps educativas", "Entretenimiento inteligente"],
    subtopics: ["Videojuegos adaptativos", "Apps de dibujo IA", "Música generada por IA"],
    content: "Exploraremos cómo los juegos se adaptan a nuestro nivel, apps que nos enseñan dibujando, y cómo la IA puede crear música. Incluye ejemplos prácticos y demostraciones.",
    image: "https://readdy.ai/api/search-image?query=Children%20playing%20educational%20games%20with%20AI%20technology%2C%20interactive%20tablets%20showing%20smart%20applications%2C%20colorful%20gaming%20interface%20with%20artificial%20intelligence%20elements&width=400&height=300&seq=ai-games-apps&orientation=landscape"
  },
  {
    id: 4,
    level: "PRINCIPIANTE",
    title: "La IA nos Ayuda a Aprender",
    description: "Herramientas de IA para mejorar el aprendizaje",
    topics: ["Tutores virtuales", "Traducción automática", "Corrección inteligente"],
    subtopics: ["Google Translate", "Corrector ortográfico", "Khan Academy"],
    content: "Aprenderemos cómo la IA nos ayuda con las tareas, traduce idiomas y nos enseña nuevas materias. Práctica con herramientas reales de traducción y tutoría virtual.",
    image: "https://readdy.ai/api/search-image?query=Children%20learning%20with%20AI%20tutors%2C%20virtual%20teacher%20helping%20students%20with%20homework%2C%20intelligent%20educational%20assistance%2C%20bright%20classroom%20with%20digital%20learning%20tools&width=400&height=300&seq=ai-learning-help&orientation=landscape"
  },
  {
    id: 5,
    level: "PRINCIPIANTE",
    title: "Creando con IA: Arte y Música",
    description: "Creatividad potenciada por inteligencia artificial",
    topics: ["Arte generado por IA", "Música automática", "Creatividad aumentada"],
    subtopics: ["DALL-E para niños", "Generadores de música", "Filtros creativos"],
    content: "Los estudiantes crearán arte y música usando herramientas de IA adaptadas para niños. Explorarán cómo la tecnología puede amplificar su creatividad natural.",
    image: "https://readdy.ai/api/search-image?query=Children%20creating%20digital%20art%20with%20AI%20tools%2C%20colorful%20artistic%20interface%2C%20kids%20making%20music%20with%20artificial%20intelligence%2C%20creative%20technology%20for%20young%20artists&width=400&height=300&seq=ai-creativity-kids&orientation=landscape"
  },

  // NOVATO - Módulos 6-10
  {
    id: 6,
    level: "NOVATO",
    title: "Cómo Funcionan los Algoritmos",
    description: "Introducción a la lógica de programación y algoritmos",
    topics: ["Secuencias lógicas", "Instrucciones paso a paso", "Resolución de problemas"],
    subtopics: ["Algoritmos de búsqueda", "Ordenamiento simple", "Decisiones lógicas"],
    content: "Aprenderemos qué son los algoritmos usando ejemplos cotidianos como recetas de cocina y instrucciones de juegos. Actividades prácticas sin programación.",
    image: "https://readdy.ai/api/search-image?query=Flowchart%20and%20algorithm%20visualization%20for%20children%2C%20colorful%20step-by-step%20process%20diagrams%2C%20logical%20thinking%20concepts%20illustrated%20for%20kids%2C%20educational%20programming%20basics&width=400&height=300&seq=algorithms-kids&orientation=landscape"
  },
  {
    id: 7,
    level: "NOVATO",
    title: "Reconocimiento de Patrones",
    description: "Cómo las máquinas identifican patrones y aprenden",
    topics: ["Patrones visuales", "Reconocimiento de formas", "Clasificación automática"],
    subtopics: ["Reconocimiento facial", "Clasificación de imágenes", "Detección de objetos"],
    content: "Exploraremos cómo las computadoras 'ven' y reconocen objetos, caras y patrones. Actividades interactivas de clasificación y reconocimiento visual.",
    image: "https://readdy.ai/api/search-image?query=Pattern%20recognition%20technology%20for%20children%2C%20visual%20classification%20systems%2C%20computer%20vision%20concepts%20for%20kids%2C%20colorful%20pattern%20matching%20interface&width=400&height=300&seq=pattern-recognition&orientation=landscape"
  },
  {
    id: 8,
    level: "NOVATO",
    title: "Datos y Información Inteligente",
    description: "Introducción al análisis de datos básico",
    topics: ["Recolección de datos", "Gráficas simples", "Interpretación básica"],
    subtopics: ["Encuestas escolares", "Gráficos de barras", "Tendencias simples"],
    content: "Los estudiantes aprenderán a recopilar información de su entorno escolar y crear gráficas básicas para entender patrones de comportamiento.",
    image: "https://readdy.ai/api/search-image?query=Children%20analyzing%20colorful%20data%20charts%20and%20graphs%2C%20simple%20statistics%20visualization%20for%20kids%2C%20educational%20data%20science%20concepts%2C%20bright%20infographic%20displays&width=400&height=300&seq=data-analysis-kids&orientation=landscape"
  },
  {
    id: 9,
    level: "NOVATO",
    title: "Comandos de Voz y Lenguaje Natural",
    description: "Cómo las máquinas entienden nuestro lenguaje",
    topics: ["Procesamiento de lenguaje", "Comandos de voz", "Chatbots básicos"],
    subtopics: ["Siri y Alexa", "Google Assistant", "Traducción automática"],
    content: "Exploraremos cómo los asistentes virtuales entienden lo que decimos y responden apropiadamente. Práctica con comandos de voz y creación de diálogos simples.",
    image: "https://readdy.ai/api/search-image?query=Children%20talking%20to%20voice%20assistants%20and%20AI%20chatbots%2C%20speech%20recognition%20technology%20for%20kids%2C%20natural%20language%20processing%20visualization%2C%20interactive%20communication%20with%20AI&width=400&height=300&seq=voice-commands-kids&orientation=landscape"
  },
  {
    id: 10,
    level: "NOVATO",
    title: "IA en la Ciencia y Medicina",
    description: "Aplicaciones de IA en campos científicos y médicos",
    topics: ["Diagnóstico médico", "Investigación científica", "Simulaciones"],
    subtopics: ["Rayos X inteligentes", "Descubrimiento de medicinas", "Modelos climáticos"],
    content: "Descubriremos cómo la IA ayuda a los doctores a detectar enfermedades y a los científicos a hacer nuevos descubrimientos para mejorar nuestras vidas.",
    image: "https://readdy.ai/api/search-image?query=AI%20in%20medicine%20and%20science%20for%20children%2C%20medical%20imaging%20technology%2C%20scientific%20research%20with%20artificial%20intelligence%2C%20healthcare%20innovation%20for%20kids%20education&width=400&height=300&seq=ai-science-medicine&orientation=landscape"
  },

  // INTERMEDIO - Módulos 11-15
  {
    id: 11,
    level: "INTERMEDIO",
    title: "Machine Learning Básico",
    description: "Introducción al aprendizaje automático",
    topics: ["Aprendizaje supervisado", "Entrenamiento de modelos", "Predicciones"],
    subtopics: ["Classification", "Regression", "Overfitting básico"],
    content: "Los estudiantes entrenarán modelos simples para hacer predicciones, usando herramientas visuales que les permitan entender cómo las máquinas aprenden de los datos.",
    image: "https://readdy.ai/api/search-image?query=Machine%20learning%20visualization%20for%20students%2C%20neural%20network%20diagrams%20for%20education%2C%20training%20data%20visualization%2C%20colorful%20ML%20concepts%20for%20teenagers&width=400&height=300&seq=machine-learning-basics&orientation=landscape"
  },
  {
    id: 12,
    level: "INTERMEDIO",
    title: "Redes Neuronales Artificiales",
    description: "Simulación del cerebro humano en computadoras",
    topics: ["Neuronas artificiales", "Capas de red", "Propagación de información"],
    subtopics: ["Perceptron", "Backpropagation", "Deep Learning intro"],
    content: "Exploraremos cómo las redes neuronales imitan el funcionamiento del cerebro humano, con simulaciones interactivas y modelos visuales de neuronas artificiales.",
    image: "https://readdy.ai/api/search-image?query=Artificial%20neural%20networks%20visualization%2C%20brain-inspired%20computing%20for%20students%2C%20colorful%20neuron%20connections%2C%20educational%20deep%20learning%20concepts&width=400&height=300&seq=neural-networks-education&orientation=landscape"
  },
  {
    id: 13,
    level: "INTERMEDIO",
    title: "Visión por Computadora",
    description: "Cómo las máquinas procesan imágenes y videos",
    topics: ["Procesamiento de imágenes", "Detección de objetos", "Realidad aumentada"],
    subtopics: ["OpenCV básico", "Filtros de imagen", "Tracking de objetos"],
    content: "Los estudiantes trabajarán con herramientas de visión computacional para crear filtros, detectar objetos y experimentar con realidad aumentada en proyectos prácticos.",
    image: "https://readdy.ai/api/search-image?query=Computer%20vision%20technology%20for%20students%2C%20image%20processing%20and%20object%20detection%2C%20AR%20visualization%20for%20education%2C%20camera-based%20AI%20systems&width=400&height=300&seq=computer-vision-students&orientation=landscape"
  },
  {
    id: 14,
    level: "INTERMEDIO",
    title: "Procesamiento de Lenguaje Natural",
    description: "IA que comprende y genera texto humano",
    topics: ["Análisis de sentimientos", "Generación de texto", "Traducción automática"],
    subtopics: ["NLP pipelines", "Tokenización", "Word embeddings"],
    content: "Crearemos chatbots simples, analizaremos sentimientos en redes sociales y exploraremos cómo la IA genera texto coherente y útil.",
    image: "https://readdy.ai/api/search-image?query=Natural%20language%20processing%20for%20education%2C%20text%20analysis%20visualization%2C%20chatbot%20development%20for%20students%2C%20language%20AI%20concepts&width=400&height=300&seq=nlp-education&orientation=landscape"
  },
  {
    id: 15,
    level: "INTERMEDIO",
    title: "IA Ética y Responsable",
    description: "Consideraciones éticas en el desarrollo de IA",
    topics: ["Sesgos en IA", "Privacidad de datos", "Impacto social"],
    subtopics: ["Fairness", "Transparencia", "Accountability"],
    content: "Analizaremos casos reales donde la IA ha tenido impactos positivos y negativos, desarrollando criterio ético para el uso responsable de la tecnología.",
    image: "https://readdy.ai/api/search-image?query=AI%20ethics%20visualization%2C%20responsible%20artificial%20intelligence%20concepts%2C%20digital%20citizenship%20for%20students%2C%20ethical%20technology%20use%20illustration&width=400&height=300&seq=ai-ethics-students&orientation=landscape"
  },

  // AVANZADO - Módulos 16-20
  {
    id: 16,
    level: "AVANZADO",
    title: "Desarrollo de Aplicaciones con IA",
    description: "Creación de apps integradas con inteligencia artificial",
    topics: ["APIs de IA", "Integración de servicios", "Desarrollo mobile"],
    subtopics: ["TensorFlow Lite", "Core ML", "Cloud AI services"],
    content: "Los estudiantes desarrollarán aplicaciones móviles sencillas que integren servicios de IA como reconocimiento de voz, visión computacional y análisis de texto.",
    image: "https://readdy.ai/api/search-image?query=Students%20developing%20AI%20applications%2C%20mobile%20app%20development%20with%20artificial%20intelligence%2C%20programming%20interface%20for%20AI%20integration%2C%20modern%20software%20development&width=400&height=300&seq=ai-app-development&orientation=landscape"
  },
  {
    id: 17,
    level: "AVANZADO",
    title: "Robótica e IA Física",
    description: "Inteligencia artificial en sistemas físicos y robots",
    topics: ["Robótica autónoma", "Sensores inteligentes", "Actuadores adaptativos"],
    subtopics: ["Arduino con IA", "Raspberry Pi", "ROS básico"],
    content: "Construiremos y programaremos robots que puedan tomar decisiones autónomas usando sensores y algoritmos de IA para navegar y interactuar con su entorno.",
    image: "https://readdy.ai/api/search-image?query=Advanced%20robotics%20with%20AI%20for%20students%2C%20autonomous%20robots%20in%20educational%20setting%2C%20intelligent%20sensors%20and%20actuators%2C%20modern%20robotics%20laboratory&width=400&height=300&seq=advanced-robotics-ai&orientation=landscape"
  },
  {
    id: 18,
    level: "AVANZADO",
    title: "Big Data y Analytics",
    description: "Manejo y análisis de grandes volúmenes de datos",
    topics: ["Bases de datos masivas", "Análisis predictivo", "Visualización avanzada"],
    subtopics: ["Hadoop básico", "Spark intro", "Data mining"],
    content: "Trabajaremos con conjuntos de datos reales para extraer insights valiosos, crear dashboards interactivos y hacer predicciones basadas en tendencias históricas.",
    image: "https://readdy.ai/api/search-image?query=Big%20data%20analytics%20visualization%2C%20large%20dataset%20processing%20for%20students%2C%20advanced%20data%20visualization%20dashboards%2C%20modern%20analytics%20tools&width=400&height=300&seq=big-data-analytics&orientation=landscape"
  },
  {
    id: 19,
    level: "AVANZADO",
    title: "IA Generativa y Creatividad",
    description: "Creación de contenido original usando IA",
    topics: ["GANs básicas", "Generación de imágenes", "Síntesis de audio"],
    subtopics: ["StyleGAN", "DALL-E 2", "Música generativa"],
    content: "Exploraremos las redes generativas adversarias y otras técnicas para crear arte, música y contenido original, experimentando con herramientas de vanguardia.",
    image: "https://readdy.ai/api/search-image?query=Generative%20AI%20art%20creation%2C%20students%20using%20AI%20for%20creative%20content%2C%20digital%20art%20generation%20tools%2C%20modern%20creative%20technology%20interface&width=400&height=300&seq=generative-ai-creativity&orientation=landscape"
  },
  {
    id: 20,
    level: "AVANZADO",
    title: "Optimización y Algoritmos Inteligentes",
    description: "Resolución de problemas complejos con IA",
    topics: ["Algoritmos genéticos", "Optimización multiobjetivo", "Búsqueda heurística"],
    subtopics: ["Simulated annealing", "Particle swarm", "Ant colony"],
    content: "Aplicaremos algoritmos bio-inspirados para resolver problemas de optimización complejos, desde logística hasta diseño de horarios escolares.",
    image: "https://readdy.ai/api/search-image?query=Algorithm%20optimization%20visualization%2C%20complex%20problem%20solving%20with%20AI%2C%20mathematical%20optimization%20for%20students%2C%20intelligent%20algorithm%20concepts&width=400&height=300&seq=ai-optimization-algorithms&orientation=landscape"
  },

  // EXPERTO - Módulos 21-24
  {
    id: 21,
    level: "EXPERTO",
    title: "Investigación en IA y Papers Científicos",
    description: "Metodología de investigación en inteligencia artificial",
    topics: ["Método científico en IA", "Revisión de literatura", "Experimentación"],
    subtopics: ["Arxiv papers", "Peer review", "Reproducibilidad"],
    content: "Los estudiantes aprenderán a leer, entender y evaluar papers científicos de IA, y comenzarán sus propios proyectos de investigación con metodología rigurosa.",
    image: "https://readdy.ai/api/search-image?query=AI%20research%20methodology%20for%20students%2C%20scientific%20papers%20and%20publications%2C%20advanced%20research%20environment%2C%20academic%20AI%20studies&width=400&height=300&seq=ai-research-methodology&orientation=landscape"
  },
  {
    id: 22,
    level: "EXPERTO",
    title: "IA en Industrias Específicas",
    description: "Aplicaciones especializadas de IA por sector",
    topics: ["IA en finanzas", "IA en salud", "IA en educación", "IA en entretenimiento"],
    subtopics: ["Algorithmic trading", "Telemedicina", "EdTech", "Gaming AI"],
    content: "Exploraremos casos de uso específicos de IA en diferentes industrias, analizando desafíos únicos y soluciones innovadoras en cada sector.",
    image: "https://readdy.ai/api/search-image?query=AI%20applications%20across%20different%20industries%2C%20specialized%20artificial%20intelligence%20in%20business%20sectors%2C%20modern%20industrial%20AI%20implementation&width=400&height=300&seq=ai-industry-applications&orientation=landscape"
  },
  {
    id: 23,
    level: "EXPERTO",
    title: "Emprendimiento y Startups de IA",
    description: "Creación de empresas basadas en tecnología de IA",
    topics: ["Business models de IA", "Funding y pitching", "Escalabilidad"],
    subtopics: ["MVP con IA", "Product-market fit", "Go-to-market"],
    content: "Los estudiantes desarrollarán planes de negocio para startups de IA, aprenderán sobre financiamiento y presentarán sus ideas ante un panel de expertos.",
    image: "https://readdy.ai/api/search-image?query=AI%20startup%20entrepreneurship%2C%20business%20presentation%20with%20artificial%20intelligence%2C%20young%20entrepreneurs%20pitching%20AI%20solutions%2C%20modern%20business%20development&width=400&height=300&seq=ai-entrepreneurship&orientation=landscape"
  },
  {
    id: 24,
    level: "EXPERTO",
    title: "Proyecto Final: Solución IA Completa",
    description: "Desarrollo integral de una solución de IA",
    topics: ["Definición del problema", "Arquitectura del sistema", "Implementación y deploy"],
    subtopics: ["Requirements analysis", "System design", "Testing y validation"],
    content: "Culminación del curso con un proyecto integral donde los estudiantes identifican un problema real y desarrollan una solución completa usando IA, desde la concepción hasta el despliegue.",
    image: "https://readdy.ai/api/search-image?query=Final%20AI%20project%20presentation%2C%20comprehensive%20artificial%20intelligence%20solution%20development%2C%20students%20showcasing%20complete%20AI%20systems%2C%20advanced%20project%20demonstration&width=400&height=300&seq=final-ai-project&orientation=landscape"
  }
];

export default function ModulosPage() {
  const [modules, setModules] = useState<Module[]>(initialModules);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [deletedModules, setDeletedModules] = useState<Module[]>([]);

  const handleEditModule = (module: Module) => {
    setEditingModule(module);
  };

  const handleSaveModule = (updatedModule: Module) => {
    setModules(modules.map(m => m.id === updatedModule.id ? updatedModule : m));
    setEditingModule(null);
  };

  const handleDeleteModule = (moduleId: number) => {
    const moduleToDelete = modules.find(m => m.id === moduleId);
    if (moduleToDelete) {
      setDeletedModules([...deletedModules, moduleToDelete]);
      setModules(modules.filter(m => m.id !== moduleId));
    }
  };

  const handleRestoreModule = (module: Module) => {
    setModules([...modules, module].sort((a, b) => a.id - b.id));
    setDeletedModules(deletedModules.filter(m => m.id !== module.id));
  };

  const groupedModules = modules.reduce((acc, module) => {
    if (!acc[module.level]) {
      acc[module.level] = [];
    }
    acc[module.level].push(module);
    return acc;
  }, {} as Record<string, Module[]>);

  const levels = ["PRINCIPIANTE", "NOVATO", "INTERMEDIO", "AVANZADO", "EXPERTO"];
  const levelColors = {
    "PRINCIPIANTE": "from-green-500 to-green-600",
    "NOVATO": "from-blue-500 to-blue-600", 
    "INTERMEDIO": "from-purple-500 to-purple-600",
    "AVANZADO": "from-red-500 to-red-600",
    "EXPERTO": "from-gray-800 to-gray-900"
  };

  // Schema.org JSON-LD for Course Modules
  useEffect(() => {
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Curso de IA - 24 Módulos",
      "description": "Desde principiante hasta experto en Inteligencia Artificial",
      "provider": {
        "@type": "Organization",
        "name": "INDEFINHUM",
        "url": import.meta.env.VITE_SITE_URL
      },
      "hasCourseInstance": levels.map(level => ({
        "@type": "CourseInstance",
        "name": `Nivel ${level}`,
        "description": level === 'PRINCIPIANTE' ? 'Fundamentos básicos de IA para todos los estudiantes' :
                      level === 'NOVATO' ? 'Conceptos intermedios y primeras aplicaciones prácticas' :
                      level === 'INTERMEDIO' ? 'Desarrollo de habilidades técnicas y proyectos' :
                      level === 'AVANZADO' ? 'Especialización en áreas específicas de IA' :
                      'Investigación avanzada y proyectos profesionales',
        "courseMode": "online",
        "educationalLevel": level
      })),
      "syllabusSections": modules.map(module => ({
        "@type": "Syllabus",
        "name": module.title,
        "description": module.description,
        "educationalLevel": module.level,
        "teaches": module.topics
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [modules]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600">
      <Header />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-700 shadow-sm border-b border-white/20">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Curso de IA - 24 Módulos</h1>
              <p className="text-blue-100 mt-1 sm:mt-2 text-sm sm:text-base">Desde principiante hasta experto en Inteligencia Artificial</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <Link 
                to="/precios" 
                className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-center text-sm sm:text-base"
              >
                Ver Precios
              </Link>
              {deletedModules.length > 0 && (
                <button 
                  onClick={() => setSelectedModule({ id: 0, level: 'PAPELERA', title: '', description: '', topics: [], subtopics: [], content: '', image: '' })}
                  className="bg-gradient-to-r from-red-400 to-red-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:from-red-500 hover:to-red-600 transition-colors duration-300 whitespace-nowrap cursor-pointer text-center text-sm sm:text-base"
                >
                  <i className="ri-delete-bin-line mr-2"></i>
                  Papelera ({deletedModules.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid gap-8 sm:gap-12">
          {levels.map(level => (
            <div key={level} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-white/20">
              <div className={`bg-gradient-to-r ${levelColors[level as keyof typeof levelColors]} p-4 sm:p-6`}>
                <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center">
                  <i className="ri-graduation-cap-line mr-2 sm:mr-3"></i>
                  Nivel {level}
                </h2>
                <p className="text-white/90 mt-2 text-sm sm:text-base">
                  {level === 'PRINCIPIANTE' && 'Fundamentos básicos de IA para todos los estudiantes'}
                  {level === 'NOVATO' && 'Conceptos intermedios y primeras aplicaciones prácticas'}
                  {level === 'INTERMEDIO' && 'Desarrollo de habilidades técnicas y proyectos'}
                  {level === 'AVANZADO' && 'Especialización en áreas específicas de IA'}
                  {level === 'EXPERTO' && 'Investigación avanzada y proyectos profesionales'}
                </p>
              </div>
              
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {groupedModules[level]?.map(module => (
                    <div key={module.id} className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow duration-300 border border-white/20">
                      <img 
                        src={module.image} 
                        alt={module.title}
                        className="w-full h-32 sm:h-40 object-cover rounded-lg mb-3 sm:mb-4"
                      />
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <h3 className="text-base sm:text-lg font-bold text-white flex-1 pr-2">{module.title}</h3>
                        <div className="flex ml-2 gap-1 sm:gap-2 flex-shrink-0">
                          <button 
                            onClick={() => setSelectedModule(module)}
                            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-gradient-to-r from-cyan-400 to-blue-5 text-white rounded-full hover:from-cyan-500 hover:to-blue-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-eye-line text-xs sm:text-sm"></i>
                          </button>
                          <button 
                            onClick={() => handleEditModule(module)}
                            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-gradient-to-r from-green-400 to-green-5 text-white rounded-full hover:from-green-500 hover:to-green-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-edit-line text-xs sm:text-sm"></i>
                          </button>
                          <button 
                            onClick={() => handleDeleteModule(module.id)}
                            className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-gradient-to-r from-red-400 to-red-5 text-white rounded-full hover:from-red-500 hover:to-red-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-xs sm:text-sm"></i>
                          </button>
                        </div>
                      </div>
                      <p className="text-blue-100 text-xs sm:text-sm mb-2 sm:mb-3">{module.description}</p>
                      <div className="text-xs text-blue-200 flex flex-wrap gap-2">
                        <span className="bg-gradient-to-r from-cyan-400/20 to-blue-500/20 backdrop-blur-sm text-cyan-300 px-2 py-1 rounded border border-cyan-300/30">Módulo {module.id}</span>
                        <span className="bg-gradient-to-r from-purple-400/20 to-pink-500/20 backdrop-blur-sm text-purple-300 px-2 py-1 rounded border border-purple-300/30">{module.topics.length} temas</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de Detalles */}
      {selectedModule && selectedModule.level !== 'PAPELERA' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/20">
            <div className="p-4 sm:p-6 border-b border-white/20">
              <div className="flex items-center justify-between">
                <h2 className="text-xl sm:text-2xl font-bold text-white pr-4">{selectedModule.title}</h2>
                <button 
                  onClick={() => setSelectedModule(null)}
                  className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-gradient-to-r from-red-400 to-red-500 rounded-full hover:from-red-500 hover:to-red-600 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-lg sm:text-xl text-white"></i>
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              <img 
                src={selectedModule.image} 
                alt={selectedModule.title}
                className="w-full h-48 sm:h-64 object-cover rounded-lg mb-4 sm:mb-6"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">Temas Principales</h3>
                  <ul className="space-y-1 sm:space-y-2">
                    {selectedModule.topics.map((topic, index) => (
                      <li key={index} className="flex items-start text-blue-100 text-sm sm:text-base">
                        <i className="ri-check-line text-green-400 mr-2 flex-shrink-0 mt-0.5"></i>
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">Subtemas</h3>
                  <ul className="space-y-1 sm:space-y-2">
                    {selectedModule.subtopics.map((subtopic, index) => (
                      <li key={index} className="flex items-start text-blue-100 text-sm sm:text-base">
                        <i className="ri-arrow-right-s-line text-cyan-400 mr-2 flex-shrink-0 mt-0.5"></i>
                        <span>{subtopic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div>
                <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">Contenido del Módulo</h3>
                <p className="text-blue-100 leading-relaxed text-sm sm:text-base">{selectedModule.content}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Papelera */}
      {selectedModule && selectedModule.level === 'PAPELERA' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/20">
            <div className="p-4 sm:p-6 border-b border-white/20">
              <div className="flex items-center justify-between">
                <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center">
                  <i className="ri-delete-bin-line mr-2 sm:mr-3 text-red-400"></i>
                  Papelera de Reciclaje
                </h2>
                <button 
                  onClick={() => setSelectedModule(null)}
                  className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-gradient-to-r from-red-400 to-red-500 rounded-full hover:from-red-500 hover:to-red-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg sm:text-xl text-white"></i>
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              {deletedModules.length === 0 ? (
                <p className="text-blue-200 text-center py-8 text-sm sm:text-base">No hay módulos eliminados</p>
              ) : (
                <div className="grid gap-3 sm:gap-4">
                  {deletedModules.map(module => (
                    <div key={module.id} className="bg-gradient-to-r from-red-400/20 to-orange-500/20 backdrop-blur-sm border border-red-300/30 rounded-lg p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div>
                        <h3 className="font-bold text-white text-sm sm:text-base">{module.title}</h3>
                        <p className="text-xs sm:text-sm text-blue-200">{module.level} - Módulo {module.id}</p>
                      </div>
                      <button 
                        onClick={() => handleRestoreModule(module)}
                        className="bg-gradient-to-r from-green-400 to-green-5 text-white px-3 sm:px-4 py-2 rounded-lg hover:from-green-500 hover:to-green-600 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base w-full sm:w-auto"
                      >
                        <i className="ri-refresh-line mr-2"></i>
                        Restaurar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Editor de Módulo */}
      {editingModule && (
        <ModuleEditor 
          module={editingModule}
          onSave={handleSaveModule}
          onCancel={() => setEditingModule(null)}
        />
      )}

      <Footer />
    </div>
  );
}
